import React from "react";

import { Form, Formik, useFormikContext } from "formik";
import Grid from "@material-ui/core/Grid";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { ConfirmDialog } from "components/ConfirmDialog";

import {
  BUSINESS_RELATION_OPTIONS,
  businessRelationsRateTypes,
  businessRelationsRateTypesAsOptions,
} from "constants/businesses";
import { BusinessFieldWithFilter } from "components/Form/Fields";
import { Button, DeleteButton, SecondaryButton } from "components/Form";
import {
  Autocomplete,
  ControlledLifeCycleInput,
  DollarDisplayCentsInputProps,
  getOptionAsValue,
  Label,
  PercentageInputProps,
  renderOptionWithDescription,
  standardFilter,
} from "components/Form/FormikControls";

import { useBoolean, useFieldValue } from "hooks";
import { validatePercentage } from "lib/validation";
import { SaleTypeField } from "components/Form/Fields/SaleFilter/SaleTypeField";
import { SpeciesField } from "components/Form/Fields/SaleFilter/SpeciesField";
import { SaleSubTypeField } from "components/Form/Fields/SaleFilter/SaleSubTypeField";
import { Divider } from "@material-ui/core";

interface InitialValuesProps {
  id?: string;
  relatedFromId?: string;
  relatedToId?: string;
  relationType?: string;
  rateValue?: number | string;
  rateType?: string;
  saleType: string;
  saleSubTypeId: number;
  speciesId: number;
}

interface BusinessRelationshipDialogProps {
  initialValues: InitialValuesProps;
  isOpen: boolean;
  handleSubmit: (object) => void;
  handleClose: () => void;
  handleDelete?: () => void;
  showRelatedFromBusiness?: boolean;
  readOnly?: boolean;
  allowDelete?: boolean;
}

interface BusinessRelationshipFormProps {
  isOpen: boolean;
  initialValues: InitialValuesProps;
  handleClose: () => void;
  handleDelete?: () => void;
  showRelatedFromBusiness?: boolean;
  readOnly?: boolean;
  allowDelete?: boolean;
}

interface RateValueInputProps {
  readOnly: boolean;
}

const RateValueInput = ({
  readOnly = false,
}: RateValueInputProps): React.JSX.Element => {
  const type = useFieldValue("rateType");

  const amountProps =
    type === businessRelationsRateTypes.HEAD
      ? DollarDisplayCentsInputProps
      : {
          ...PercentageInputProps,
          fieldDecimalPlaces: 6,
          decimalPlacesLimit: 4,
        };

  return (
    <ControlledLifeCycleInput
      name="rateValue"
      label="Value"
      readOnly={readOnly}
      {...amountProps}
    />
  );
};

const baseValidationShape = {
  relatedToId: Yup.string().nullable().required("Required"),
  relationType: Yup.string().nullable().required("Required"),
  rateValue: validatePercentage(12, 6),
  rateType: Yup.string().nullable().required("Required"),
};

const validationSchema = Yup.object().shape(baseValidationShape);
const validationSchemaWithRelatedFrom = Yup.object().shape({
  relatedFromId: Yup.string().nullable().required("Required"),
  ...baseValidationShape,
});

function BusinessRelationshipForm({
  isOpen,
  handleDelete,
  handleClose,
  initialValues,
  showRelatedFromBusiness,
  readOnly,
  allowDelete,
}: BusinessRelationshipFormProps) {
  const [isConfirmCloseOpen, openConfirmClose, closeConfirmClose] =
    useBoolean(false);

  const { setFieldValue, setFieldTouched, dirty } = useFormikContext();

  const doHandleClose = (): void => {
    if (dirty) {
      openConfirmClose();
    } else {
      handleClose();
    }
  };

  const handleConfirmDiscard = (): void => {
    closeConfirmClose();
    handleClose();
  };

  const clearRateValue = () => {
    setFieldValue("rateValue", null);
    setFieldTouched("rateValue");
  };

  return (
    <>
      <Dialog open={isOpen} maxWidth="md" fullWidth onClose={doHandleClose}>
        <Form data-tour="businessRelationshipForm">
          <DialogTitle
            onClose={doHandleClose}
          >{`${initialValues?.id ? "Edit" : "Add"} Relationship`}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {showRelatedFromBusiness ? (
                <Grid item xs={12}>
                  <BusinessFieldWithFilter
                    name="relatedFromId"
                    label="Source Business"
                    disabled={readOnly}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Autocomplete
                  name="relationType"
                  label="Relation Type"
                  options={BUSINESS_RELATION_OPTIONS}
                  renderOption={renderOptionWithDescription}
                  getOptionValue={getOptionAsValue}
                  filterOptions={standardFilter}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <BusinessFieldWithFilter
                  name="relatedToId"
                  label="Related To Business"
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  name="rateType"
                  label="Rate Type"
                  options={businessRelationsRateTypesAsOptions}
                  getOptionValue={getOptionAsValue}
                  filterOptions={standardFilter}
                  onChangeExtra={clearRateValue}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <RateValueInput readOnly={readOnly} />
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Label tooltip="Set Relationship per type of Sale. Sale Sub Type will be preferred over Species, then Sale Type, then the default, if set.">
                  Apply to Sales
                </Label>
              </Grid>

              <Grid item xs={12} md={4}>
                <SaleTypeField label="Sale Type" disabled={readOnly} />
              </Grid>
              <Grid item xs={12} md={4}>
                <SpeciesField label="Species" disabled={readOnly} />
              </Grid>

              <Grid item xs={12} md={4}>
                <SaleSubTypeField label="Sale Sub Type" disabled={readOnly} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {handleDelete && (
              <DeleteButton
                data-tour="delete"
                onClick={handleDelete}
                type="button"
                disabled={!allowDelete}
              >
                Delete
              </DeleteButton>
            )}
            <SecondaryButton
              data-tour="cancel"
              onClick={handleClose}
              type="button"
            >
              Cancel
            </SecondaryButton>
            <Button data-tour="save" type="submit" disabled={readOnly}>
              Save
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
      <ConfirmDialog
        title="Are you sure you want to exit?"
        message="You will lose your unsaved changes."
        isOpen={isConfirmCloseOpen}
        onCancel={closeConfirmClose}
        onDelete={handleConfirmDiscard}
        buttonMessage="Confirm"
        cancelMessage="Back"
      />
    </>
  );
}

export const BusinessRelationshipDialog = ({
  initialValues,
  isOpen,
  handleSubmit,
  handleClose,
  handleDelete,
  showRelatedFromBusiness,
  readOnly = false,
  allowDelete = false,
}: BusinessRelationshipDialogProps) => (
  <Formik
    enableReinitialize
    initialValues={initialValues}
    onSubmit={!readOnly ? handleSubmit : () => {}}
    validationSchema={
      showRelatedFromBusiness
        ? validationSchemaWithRelatedFrom
        : validationSchema
    }
  >
    <BusinessRelationshipForm
      isOpen={isOpen}
      handleDelete={handleDelete}
      handleClose={handleClose}
      initialValues={initialValues}
      showRelatedFromBusiness={showRelatedFromBusiness}
      readOnly={readOnly}
      allowDelete={allowDelete}
    />
  </Formik>
);
